import { clientPhotosConstants } from '../_constants/clientPhotos.constants';

const emptyClientState = () => ({
    items: [],
    nextCursor: null,
    loading: false,
    loadingMore: false,
    error: null,
    consent: { givenAt: null, givenByUserId: null },
    // Map<tempId, optimisticPhoto> for optimistic uploads that haven't resolved
    pendingUploads: [],
    // Map<photoId, photo> of photos pending final delete (inside undo window)
    pendingDeletes: {},
});

const initialState = {
    byClientId: {},
    salonTags: [], // [{ name, count }], sorted by count desc
    // Cache of service-specific tag rankings keyed by serviceName.
    // Each entry: [{ name, count }] learned from photos linked to that service.
    contextTagsByService: {},
};

const getClientState = (state, clientId) => state.byClientId[clientId] || emptyClientState();

const updateClient = (state, clientId, patch) => ({
    ...state,
    byClientId: {
        ...state.byClientId,
        [clientId]: {
            ...getClientState(state, clientId),
            ...patch,
        },
    },
});

export function clientPhotos(state = initialState, action) {
    switch (action.type) {
        case clientPhotosConstants.LOAD_REQUEST: {
            const current = getClientState(state, action.clientId);
            const isFirstPage = !action.cursor;
            return updateClient(state, action.clientId, {
                loading: isFirstPage,
                loadingMore: !isFirstPage,
                error: null,
                // on a first-page load we reset the list so refetches don't
                // stack duplicates
                items: isFirstPage ? [] : current.items,
                nextCursor: isFirstPage ? null : current.nextCursor,
            });
        }

        case clientPhotosConstants.LOAD_SUCCESS: {
            const current = getClientState(state, action.clientId);
            const incoming = action.photos || [];
            const existingIds = new Set(current.items.map((p) => p.id));
            const deduped = incoming.filter((p) => !existingIds.has(p.id));
            return updateClient(state, action.clientId, {
                items: [...current.items, ...deduped],
                nextCursor: action.nextCursor,
                consent: action.consent || current.consent,
                loading: false,
                loadingMore: false,
                error: null,
            });
        }

        case clientPhotosConstants.LOAD_FAILURE:
            return updateClient(state, action.clientId, {
                loading: false,
                loadingMore: false,
                error: action.error,
            });

        case clientPhotosConstants.UPLOAD_OPTIMISTIC: {
            const current = getClientState(state, action.clientId);
            return updateClient(state, action.clientId, {
                pendingUploads: [...current.pendingUploads, action.optimistic],
            });
        }

        case clientPhotosConstants.UPLOAD_SUCCESS: {
            const current = getClientState(state, action.clientId);
            return updateClient(state, action.clientId, {
                items: [action.photo, ...current.items],
                pendingUploads: current.pendingUploads.filter((p) => p.tempId !== action.tempId),
            });
        }

        case clientPhotosConstants.UPLOAD_FAILURE: {
            const current = getClientState(state, action.clientId);
            return updateClient(state, action.clientId, {
                pendingUploads: current.pendingUploads.map((p) =>
                    p.tempId === action.tempId ? { ...p, error: action.error } : p,
                ),
            });
        }

        case clientPhotosConstants.PATCH_OPTIMISTIC: {
            const current = getClientState(state, action.clientId);
            return updateClient(state, action.clientId, {
                items: current.items.map((p) =>
                    p.id === action.photoId ? { ...p, tags: action.tags } : p,
                ),
            });
        }

        case clientPhotosConstants.PATCH_SUCCESS:
            // server already reflects the optimistic state
            return state;

        case clientPhotosConstants.PATCH_FAILURE: {
            const current = getClientState(state, action.clientId);
            return updateClient(state, action.clientId, {
                items: current.items.map((p) =>
                    p.id === action.photoId ? { ...p, tags: action.previousTags } : p,
                ),
            });
        }

        // Share consent (social media): the answer shows at once; the
        // server's record (who, when) replaces it, or the old one comes back.
        case clientPhotosConstants.SHARE_CONSENT_OPTIMISTIC:
        case clientPhotosConstants.SHARE_CONSENT_SUCCESS:
        case clientPhotosConstants.SHARE_CONSENT_FAILURE: {
            const current = getClientState(state, action.clientId);
            return updateClient(state, action.clientId, {
                items: current.items.map((p) =>
                    p.id === action.photoId ? { ...p, ...action.patch } : p,
                ),
            });
        }

        case clientPhotosConstants.SALON_TAGS_LOAD_SUCCESS:
            return { ...state, salonTags: action.tags };

        case clientPhotosConstants.CONTEXT_TAGS_LOAD_SUCCESS:
            return {
                ...state,
                contextTagsByService: {
                    ...state.contextTagsByService,
                    [action.service]: action.tags,
                },
            };

        case clientPhotosConstants.DELETE_OPTIMISTIC: {
            const current = getClientState(state, action.clientId);
            const removed = current.items.find((p) => p.id === action.photoId);
            return updateClient(state, action.clientId, {
                items: current.items.filter((p) => p.id !== action.photoId),
                pendingDeletes: removed
                    ? { ...current.pendingDeletes, [action.photoId]: removed }
                    : current.pendingDeletes,
            });
        }

        case clientPhotosConstants.DELETE_COMMIT: {
            const current = getClientState(state, action.clientId);
            const { [action.photoId]: _removed, ...rest } = current.pendingDeletes;
            return updateClient(state, action.clientId, { pendingDeletes: rest });
        }

        case clientPhotosConstants.DELETE_UNDO: {
            const current = getClientState(state, action.clientId);
            const restored = current.pendingDeletes[action.photoId];
            if (!restored) return state;
            const { [action.photoId]: _removed, ...rest } = current.pendingDeletes;
            // Re-insert keeping newest-first order by capturedAt.
            const items = [...current.items, restored].sort(
                (a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime(),
            );
            return updateClient(state, action.clientId, { items, pendingDeletes: rest });
        }

        case clientPhotosConstants.DELETE_FAILURE: {
            // restore like undo, but no user interaction needed
            const current = getClientState(state, action.clientId);
            const restored = current.pendingDeletes[action.photoId];
            if (!restored) return state;
            const { [action.photoId]: _removed, ...rest } = current.pendingDeletes;
            const items = [...current.items, restored].sort(
                (a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime(),
            );
            return updateClient(state, action.clientId, {
                items,
                pendingDeletes: rest,
                error: action.error,
            });
        }

        case clientPhotosConstants.CONSENT_SET:
            return updateClient(state, action.clientId, { consent: action.consent });

        case clientPhotosConstants.CONSENT_REVOKE:
            return updateClient(state, action.clientId, {
                items: [],
                pendingUploads: [],
                pendingDeletes: {},
                consent: { givenAt: null, givenByUserId: null },
                nextCursor: null,
            });

        default:
            return state;
    }
}
